import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Paper, Typography } from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../context/context';
import { getToken } from '../authentication/functions/authentication_management';
import type { DecodedAuthToken } from '../authentication/types/AuthTypes';
import {
  licenseBlockingMessage,
  licenseExpiryWarning,
  readLicenseFromDecoded,
} from '../../services/licensing/jwtLicense';
import { subscribeLicense403 } from '../../services/licensing/license403Bus';

/**
 * Global license messaging from JWT and from license-related API 403s.
 */
export default function LicenseBanner(): React.ReactElement | null {
  const { t } = useTranslation('licensing');
  const ctx = useContext(AppContext);
  const userDetails = ctx?.userData?.userDetails;
  const [api403Message, setApi403Message] = useState<string | null>(null);

  useEffect(() => {
    return subscribeLicense403((detail) => {
      setApi403Message(detail);
    });
  }, []);

  const { blockMsg, warnMsg, isAdmin } = useMemo(() => {
    if (!userDetails?.userId) {
      return { blockMsg: null as string | null, warnMsg: null as string | null, isAdmin: false };
    }
    const token = getToken();
    if (!token?.access_token) {
      return { blockMsg: null, warnMsg: null, isAdmin: false };
    }
    try {
      const decoded = jwtDecode<DecodedAuthToken>(token.access_token);
      const isAdminUser = decoded.is_admin === true || userDetails.role === 'admin';
      const lic = readLicenseFromDecoded(decoded);
      return {
        blockMsg: licenseBlockingMessage(lic, isAdminUser),
        warnMsg: licenseExpiryWarning(lic, isAdminUser),
        isAdmin: isAdminUser,
      };
    } catch {
      return { blockMsg: null, warnMsg: null, isAdmin: false };
    }
  }, [userDetails?.userId, userDetails?.role]);

  const effectiveBlock = blockMsg ?? (!isAdmin ? api403Message : null);

  if (!effectiveBlock && !warnMsg) return null;

  if (effectiveBlock) {
    return (
      <Box
        role="alertdialog"
        aria-live="assertive"
        aria-label={t('banner.ariaLabel')}
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: (theme) => theme.zIndex.modal + 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          bgcolor: 'rgba(2, 6, 23, 0.6)',
          backdropFilter: 'blur(2px)',
          WebkitBackdropFilter: 'blur(2px)',
        }}
      >
        <Paper
          elevation={8}
          sx={{
            width: 'min(560px, 100%)',
            p: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'error.light',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="h6" color="error.main" sx={{ fontWeight: 700, mb: 1 }}>
            {t('banner.accessRestricted')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            {effectiveBlock}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('banner.lockedHint')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      {warnMsg && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {warnMsg}
        </Alert>
      )}
    </Box>
  );
}
